import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, SectionTitle, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider, Separator, Result } from '../../../../components/course/CourseComponents';

const Chapitre1Content = ({ courseId = 'snake' }) => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Mise en place du squelette du jeu Snake avec le chargement des assets et l'affichage du fond
        </p>
      </InfoBox>

      <Separator type="text" text="Préambule" color="gray-darker" size="md" />
      <SectionTitle>🐍 1/ Introduction au jeu Snake</SectionTitle>

      <p>Dans ce cours, nous allons apprendre à coder un jeu de type Snake.</p>
      <p>Le joueur contrôle un petit serpent qui avance automatiquement. Il peut le faire changer de direction avec les touches directionnelles. À chaque fois que le serpent mange une pomme, il grandit. Si le serpent touche un mur ou son propre corps, la partie est terminée.</p>
      <p>Le but du jeu est de manger le plus de pommes possible.</p>

      <p>Il existe plusieurs façons de faire ce genre de jeu, la plus connue étant celle qui fonctionne par cases : le canvas est divisé en 20 lignes et 20 colonnes, et le serpent se déplace case par case.</p>

      <p>Ce n'est pas cette méthode que j'ai retenue, car elle rend le déplacement du serpent saccadé.<br/>
      Je souhaite vous apprendre à faire un Snake fluide, qui n'est pas limité à un déplacement par cases.</p>

      <Separator type="text" text="Idée générale" color="gray-darker" size="md" />
      <SectionTitle>⚙️ 2/ Principe de fonctionnement</SectionTitle>

      <p>Pour cela, on fait avancer la tête à une certaine vitesse et on enregistre dans une liste la position de la tête à chaque frame.</p>
      <p>Ensuite, on affiche le corps du serpent en parcourant la liste.</p>
      <p>La liste est limitée à une certaine taille (par exemple 100 positions). Quand on mange une pomme, on augmente cette taille autorisée (par exemple 120).</p>
      <p>La taille de la liste représente donc la longueur du serpent.</p>

      
      <p>Pour éviter que le jeu soit trop gourmand quand le serpent devient très long, on affiche un morceau de corps toutes les 4 positions de la liste.<br/>
      Cela réduit le nombre d'éléments à afficher.</p>

      <p>En termes de proportions, on utilisera une tête et un corps de 40x40 pixels sur un canvas de 500x500.</p>

      <p>Pour détecter une collision avec les bords du canvas, on vérifie si la tête du serpent sort des limites de l'écran.<br/>
      Pour tester les collisions avec soi-même, on regarde si la distance entre la tête et un morceau du corps est inférieure à la largeur du serpent (40px).</p>

      

      <Separator type="text" text="Squelette du jeu" color="gray-darker" size="md" />
      <SectionTitle>🏗️ 3/ Construction du squelette</SectionTitle>

      <p>Dans ce premier chapitre, nous allons construire le squelette du jeu :</p>

      <ul className="list-disc pl-6 mb-4">
        <li>Charger toutes les images et sons nécessaires</li>
        <li>Créer un canvas de 500x500</li>
        <li>Afficher une image de fond</li>
        <li>Se préparer à récupérer les entrées clavier du joueur</li>
      </ul>

      <p><strong>Les assets nécessaires :</strong></p>

      <ul className="list-disc pl-6 mb-4">
        <li>Une image de fond</li>
        <li>Trois images pour la tête du serpent (animation + mort)</li>
        <li>Une image pour le corps du serpent</li>
        <li>Une image de pomme</li>
        <li>Deux sons : un pour quand on croque une pomme, un pour quand on meurt</li>
      </ul>

      <Note type="info" title="📁 Assets disponibles">
        <p>Tous ces assets sont disponibles dans l'Asset Manager, sous le bouton violet <strong>Play</strong>.</p>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer">
        <p>- Crée les variables globales pour contenir toutes les images et sons :</p>

        <CodeBlock language="javascript">
{`let imageFond;
let imageTete1;
let imageTete2;
let imageTeteMort;
let imageCorps;
let imagePomme;
let sonMort;
let sonCroque;`}
        </CodeBlock>

        <p>- Dans la fonction preload(), charge toutes les images et sons :</p>

        <CodeBlock language="javascript">
{`//--------------------------------- PRELOAD ------------------------------------------
function preload(){
  imageFond = loadImage('fond.png');
  imageTete1 = loadImage('snake-tete1.png');
  imageTete2 = loadImage('snake-tete2.png');
  imageTeteMort = loadImage('snake-tete-mort.png');
  imageCorps = loadImage('snake-corps.png');
  imagePomme = loadImage('pomme.png');
  sonMort = loadSound('mort.wav');
  sonCroque = loadSound('croque.wav');
}`}
        </CodeBlock>

        <p>- Dans la fonction setup(), crée le canvas 500x500 :</p>

        <CodeBlock language="javascript">
{`//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(500, 500);
}`}
        </CodeBlock>

        <p>- Dans la fonction draw(), defini l'origine des images en haut à gauche avec <InlineCode>imageMode(CORNER)</InlineCode> et affiche le fond :</p>

        <CodeBlock language="javascript">
{`//--------------------------------- DRAW ------------------------------------------
function draw() {
  imageMode(CORNER);
  background(imageFond);
}`}
        </CodeBlock>

        <p>- Prépare la fonction keyPressed() appelée à chaque fois que le joueur appuie sur une touche :</p>

        <CodeBlock language="javascript">
{`//--------------------------------- CLAVIER ------------------------------------------
function keyPressed() {

}`}
        </CodeBlock>

        <br/>
        <Result>Un canvas de 500 par 500 avec un fond de type herbe.</Result>
      </Note>

      <Note type="tip" title="💡 Tips : à quoi sert imageMode(CORNER) ?">
        <p>Cela permet de définir l'origine de l'image (0,0) en haut à gauche. Par défaut, le mode utilisé est déjà <InlineCode>CORNER</InlineCode>, qui place l'image à partir du coin supérieur gauche.</p>
        <p>Mais ici, on le précise, car plus tard on utilisera <InlineCode>imageMode(CENTER)</InlineCode> pour placer l'origine de la tête du serpent au centre de l'image.</p>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Squelette et chargement des assets</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 4 : Snake
let imageFond;
let imageTete1;
let imageTete2;
let imageTeteMort;
let imageCorps;
let imagePomme;
let sonMort;
let sonCroque;

//--------------------------------- PRELOAD ------------------------------------------
function preload(){
  imageFond = loadImage('fond.png');
  imageTete1 = loadImage('snake-tete1.png');
  imageTete2 = loadImage('snake-tete2.png');
  imageTeteMort = loadImage('snake-tete-mort.png');
  imageCorps = loadImage('snake-corps.png');
  imagePomme = loadImage('pomme.png');
  sonMort = loadSound('mort.wav');
  sonCroque = loadSound('croque.wav');
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(500, 500);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  imageMode(CORNER);
  background(imageFond);
}

//--------------------------------- CLAVIER ------------------------------------------
function keyPressed() {

}`}
      </CodeBlock>

      <p>🎯 Dans le prochain chapitre, nous allons gérer le déplacement de la tête du serpent !</p>
    </>
  );
};

export default Chapitre1Content; 